import React, { useState } from "react";
import {
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, ContentCopy } from "@mui/icons-material";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import {
  type SdkPromptRevision,
  type SdkSessionReview,
  type SdkSessionReviewClassification,
  type Session as SessionType,
} from "@/utils/types";

type ChipColor = "default" | "error" | "info" | "success" | "warning";

const REVIEW_LABELS: Record<SdkSessionReviewClassification, string> = {
  legitimate_pass: "Correctly succeeded",
  false_positive_pass: "False positive",
  legitimate_failure: "Correctly failed",
  false_alarm_failure: "Falsely failed",
};

const REVIEW_COLORS: Record<SdkSessionReviewClassification, ChipColor> = {
  legitimate_pass: "success",
  false_positive_pass: "error",
  legitimate_failure: "default",
  false_alarm_failure: "warning",
};

export type SessionReviewBadge = {
  label: string;
  color: ChipColor;
  tooltip?: string;
};

// Single source of truth for how an SDK-session review surfaces in the UI
// (the sessions-list "Auto-review" column and the session details tab).
export function getSessionReviewBadge(
  review?: SdkSessionReview
): SessionReviewBadge | null {
  if (!review) return null;
  if (review.status === "in_progress") {
    return { label: "In progress", color: "info" };
  }
  if (review.status === "error") {
    return {
      label: "Review error",
      color: "error",
      tooltip: review.error ?? undefined,
    };
  }
  if (review.status === "completed" && review.classification) {
    return {
      label: REVIEW_LABELS[review.classification],
      color: REVIEW_COLORS[review.classification],
      tooltip: review.reasoning || undefined,
    };
  }
  return null;
}

// Plain-string label for the data-grid column (drives quick-search + sorting).
export function getSessionReviewLabel(review?: SdkSessionReview): string {
  return getSessionReviewBadge(review)?.label ?? "";
}

// Options for the sessions-list "Auto-review" filter dropdown. `value` is
// either a classification, or the synthetic "in_progress" / "not_reviewed".
export const SESSION_REVIEW_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "false_positive_pass", label: "False positive" },
  { value: "false_alarm_failure", label: "Falsely failed" },
  { value: "legitimate_pass", label: "Correctly succeeded" },
  { value: "legitimate_failure", label: "Correctly failed" },
  { value: "in_progress", label: "In progress" },
  { value: "not_reviewed", label: "Not reviewed" },
];

export function sessionMatchesReviewFilter(
  review: SdkSessionReview | undefined,
  filter: string
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "not_reviewed":
      return !review;
    case "in_progress":
      return review?.status === "in_progress";
    default:
      return review?.status === "completed" && review?.classification === filter;
  }
}

// Copy-to-clipboard button with a brief "Copied!" confirmation.
const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard can be unavailable (e.g. insecure context) — fail silently
    }
  };
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label="Copy new prompt"
        disabled={!text}
      >
        {copied ? (
          <Check fontSize="small" color="success" />
        ) : (
          <ContentCopy fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

// One before/after prompt change.
const PromptRevisionCard = ({ revision }: { revision: SdkPromptRevision }) => (
  <RevisionCard>
    <RevisionColumns>
      <RevisionColumn>
        <RevisionHeader>
          <RevisionLabel>Old prompt</RevisionLabel>
        </RevisionHeader>
        <OldPromptText>{revision.oldPrompt || "—"}</OldPromptText>
      </RevisionColumn>
      <RevisionColumn>
        <RevisionHeader>
          <RevisionLabel>New prompt</RevisionLabel>
          <CopyButton text={revision.newPrompt} />
        </RevisionHeader>
        <NewPromptText>{revision.newPrompt || "—"}</NewPromptText>
      </RevisionColumn>
    </RevisionColumns>
    {revision.reason && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.75 }}
      >
        {revision.reason}
      </Typography>
    )}
  </RevisionCard>
);

// Grey, padded scroll container + an inset Paper card — mirrors how the session
// content panel renders a single interaction (ContentWrapper + LogItemWrapper).
const ReviewLayout = ({ children }: { children: React.ReactNode }) => (
  <ReviewScroll>
    <ReviewPaper>{children}</ReviewPaper>
  </ReviewScroll>
);

// Details-page tab content: shows the verdict, reasoning and suggested prompt
// changes once the review is ready (or an in-progress / error / empty state
// otherwise).
const SessionAutoReview = ({ session }: { session: SessionType }) => {
  const review = session.autoReview;

  if (!review) {
    return (
      <ReviewLayout>
        <Typography variant="body2" color="text.secondary">
          No auto-review for this session. Auto-review runs after a session
          finishes when it was explicitly marked succeeded or failed.
        </Typography>
      </ReviewLayout>
    );
  }

  if (review.status === "in_progress") {
    return (
      <ReviewLayout>
        <SpinnerRow>
          <CircularProgress size={18} />
          <Typography variant="body2">Review in progress…</Typography>
        </SpinnerRow>
      </ReviewLayout>
    );
  }

  if (review.status === "error") {
    return (
      <ReviewLayout>
        <Alert severity="error">
          {review.error || "The auto-review failed to complete."}
        </Alert>
      </ReviewLayout>
    );
  }

  const badge = getSessionReviewBadge(review);
  const revisions = review.promptRevisions ?? [];
  const legacyImprovements = review.suggestedPromptImprovements ?? [];

  return (
    <ReviewLayout>
      <VerdictRow>
        <Typography variant="subtitle2">Verdict</Typography>
        {badge && (
          <Chip size="small" color={badge.color} label={badge.label} />
        )}
        {typeof review.confidence === "number" && (
          <Typography variant="body2" color="text.secondary">
            Confidence: {Math.round(review.confidence * 100)}%
          </Typography>
        )}
      </VerdictRow>

      {review.reasoning && (
        <Section>
          <Typography variant="subtitle2" gutterBottom>
            Reasoning
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {review.reasoning}
          </Typography>
        </Section>
      )}

      {revisions.length > 0 && (
        <Section>
          <Typography variant="subtitle2" gutterBottom>
            Suggested prompt changes
          </Typography>
          <RevisionList>
            {revisions.map((rev, idx) => (
              <PromptRevisionCard key={idx} revision={rev} />
            ))}
          </RevisionList>
        </Section>
      )}

      {revisions.length === 0 && legacyImprovements.length > 0 && (
        <Section>
          <Typography variant="subtitle2" gutterBottom>
            Suggested prompt improvements
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {legacyImprovements.map((item, idx) => (
              <li key={idx}>
                <Typography variant="body2">{item}</Typography>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {review.reviewedAt && (
        <Typography variant="caption" color="text.secondary">
          Reviewed{review.reviewedStatus ? ` ${review.reviewedStatus}` : ""}{" "}
          session ·{" "}
          {DateTime.fromMillis(review.reviewedAt).toFormat(
            "yyyy-MM-dd hh:mm:ss a"
          )}
        </Typography>
      )}
    </ReviewLayout>
  );
};

const ReviewScroll = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px;
`;

const ReviewPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const VerdictRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SpinnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const RevisionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RevisionCard = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 10px 12px;
  background-color: #fff;
`;

const RevisionColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const RevisionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const RevisionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 30px;
`;

const RevisionLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.5);
`;

const promptTextBase = `
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  padding: 8px;
  border-radius: 4px;
`;

const OldPromptText = styled.div`
  ${promptTextBase}
  background-color: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.6);
`;

const NewPromptText = styled.div`
  ${promptTextBase}
  background-color: rgba(46, 160, 67, 0.08);
`;

export default SessionAutoReview;
